import { Box, Center, Flex } from 'front-end/src/components'
import { formatDateShorter } from 'front-end/src/utils/dateHelpers'
import React, { FC } from 'react'
import { isColorDark } from 'shared/colorUtils'

import { FlexProps } from '@chakra-ui/react'
import { t } from '@lingui/macro'

import { brandColors } from '../../../../../shared/basicPalette'
import IconCheck from '../icon/IconCheck'
import IconClock from '../icon/IconClock'
import IconClose from '../icon/IconClose'
import IconMagicWand from '../icon/IconMagicWand'
import { IconCircleEmpty } from '../icon/IconCircleEmpty'
import { IconCircleFull } from '../icon/IconCircleFull'
import { IconCircleProgress } from '../icon/IconCircleProgress'
import { IconCircleNotStarted } from '../icon/IconCircleNotStarted'
import {
  CertificationEnrolmentStatusEnum,
  WorkbookStatusEnum
} from '../../../graphql/private/PrivateSchemaTypes'
import { PathwayEnrolmentStatusEnum } from '../../../graphql/public/PublicSchemaTypes'
import { IconCheckFull } from '../icon/IconCheckFull'

type statusVariant = 'grey' | 'red' | 'blue' | 'green' | 'white'

const getColorVariant = (color?: statusVariant) => {
  switch (color) {
    case 'grey': {
      return brandColors.grey[75]
    }
    case 'red': {
      return brandColors.red[500]
    }
    case 'blue': {
      return brandColors.blue[500]
    }
    case 'green': {
      return brandColors.green[700]
    }
    case 'white': {
      return brandColors.white
    }
  }
}

export const Status: FC<
  FlexProps & {
    text: string
    variant?: statusVariant
    colorText?: string
    icon?: JSX.Element
  }
> = (props) => {
  const { text, variant = 'white', colorText, icon, ...rest } = props
  return (
    <Flex
      data-test="Status"
      display="inline-flex"
      alignItems="center"
      h="22px"
      fontSize="12px"
      fontWeight="normal"
      whiteSpace="nowrap"
      maxW="100%"
      color={
        isColorDark(getColorVariant(variant))
          ? brandColors.white
          : brandColors.grey[600]
      }
      bg={getColorVariant(variant)}
      borderRadius="11px"
      p={icon ? '3px 10px 3px 6px' : '3px 10px'}
      border={
        variant === 'white' ? `1px solid ${brandColors.grey[400]}` : 'none'
      }
      {...rest}
    >
      {icon && <Center mr="6px">{icon}</Center>}
      <Box color={colorText}>{text && text}</Box>
    </Flex>
  )
}

// Progress variants

export const StatusProgressNotStarted: FC<FlexProps> = (props) => (
  <Status
    text={t`Not started`}
    icon={<IconCircleNotStarted size="sm" />}
    {...props}
  />
)

export const StatusProgressNew: FC<FlexProps> = (props) => (
  <Status text={t`New`} icon={<IconMagicWand />} {...props} />
)

export const StatusProgressUpdated: FC<FlexProps> = (props) => (
  <Status text={t`Updated`} icon={<IconMagicWand />} {...props} />
)

export const StatusProgressCompleted: FC<FlexProps> = (props) => (
  <Status text={t`Completed`} variant="green" icon={<IconCheck />} {...props} />
)

export const StatusProgressCompletedInverted: FC<FlexProps> = (props) => (
  <Status
    text={t`Completed`}
    variant="white"
    borderColor={brandColors.green[700]}
    color={brandColors.green[700]}
    icon={<IconCheckFull size="sm" />}
    {...props}
  />
)

export const StatusEventAttended: FC<FlexProps> = (props) => (
  <Status
    text={t`Attended`}
    variant="white"
    borderColor={brandColors.green[700]}
    color={brandColors.green[700]}
    icon={<IconCheckFull size="sm" />}
    {...props}
  />
)

export const StatusProgressPassed: FC<FlexProps> = (props) => (
  <Status text={t`Passed`} variant="green" icon={<IconCheck />} {...props} />
)
export const StatusProgressFailed: FC<FlexProps> = (props) => (
  <Status text={t`Failed`} variant="red" icon={<IconClose />} {...props} />
)

export const StatusProgressInProgress: FC<FlexProps> = (props) => (
  <Status
    text={t`In progress`}
    variant="blue"
    icon={<IconCircleProgress size="sm" />}
    {...props}
  />
)

export const StatusProgressInProgressRed: FC<FlexProps> = (props) => (
  <Status
    text={t`In progress`}
    variant="red"
    icon={<IconCircleProgress size="sm" />}
    {...props}
  />
)

export const StatusProgressDue: FC<
  FlexProps & {
    dueDate?: string
    status:
      | WorkbookStatusEnum
      | PathwayEnrolmentStatusEnum
      | CertificationEnrolmentStatusEnum
  }
> = (props) => {
  const { dueDate = false, status } = props
  return (
    <Status
      text={`${status === WorkbookStatusEnum.OVERDUE ? t`Overdue` : t`Due on`}${
        dueDate ? ` ${formatDateShorter(dueDate)}` : ''
      }`}
      variant={
        status === WorkbookStatusEnum.OVERDUE
          ? 'red'
          : status === WorkbookStatusEnum.IN_PROGRESS
          ? 'blue'
          : 'white'
      }
      icon={<IconClock />}
    />
  )
}

// Grey variants

export const StatusDraftGrey: FC<FlexProps> = (props) => (
  <Status
    text={t`Draft`}
    variant="grey"
    icon={<IconCircleEmpty />}
    {...props}
  />
)

export const StatusPublishedGrey: FC<FlexProps> = (props) => (
  <Status
    text={t`Published`}
    variant="grey"
    color={brandColors.green[700]}
    icon={<IconCircleFull />}
    {...props}
  />
)

export const StatusChangedGrey: FC<FlexProps> = (props) => (
  <Status
    text={t`Changed`}
    variant="grey"
    color={brandColors.blue.mid}
    icon={<IconCircleEmpty />}
    {...props}
  />
)

export const StatusInProgressGrey: FC<FlexProps> = (props) => (
  <Status
    text={t`In progress`}
    variant="grey"
    color={brandColors.blue.mid}
    icon={<IconCircleFull />}
    {...props}
  />
)

export const StatusUnpublishedChangesGrey: FC<FlexProps> = (props) => (
  <Status
    text={t`Unpublished changes`}
    variant="grey"
    color={brandColors.blue.mid}
    icon={<IconCircleEmpty />}
    {...props}
  />
)

export const StatusArchivedGrey: FC<FlexProps> = (props) => (
  <Status
    text={t`Archived`}
    variant="grey"
    color={brandColors.grey[400]}
    icon={<IconCircleFull />}
    {...props}
  />
)

export const StatusClosedGrey: FC<FlexProps> = (props) => (
  <Status
    text={t`Closed`}
    variant="grey"
    color={brandColors.grey[400]}
    icon={<IconCircleFull />}
    {...props}
  />
)

export const StatusLatestGrey: FC<FlexProps> = (props) => (
  <Status
    text={t`Latest`}
    variant="grey"
    color={brandColors.green[700]}
    colorText={brandColors.green[800]}
    icon={<IconCircleFull />}
    {...props}
  />
)

export const StatusOpenGrey: FC<FlexProps> = (props) => (
  <Status
    text={t`Open`}
    variant="grey"
    color={brandColors.green[700]}
    colorText={brandColors.green[800]}
    icon={<IconCircleFull />}
    {...props}
  />
)

export const StatusActiveGrey: FC<FlexProps> = (props) => (
  <Status
    text={t`Active`}
    variant="grey"
    color={brandColors.green[700]}
    colorText={brandColors.green[800]}
    icon={<IconCircleFull />}
    {...props}
  />
)

export const StatusInactiveGrey: FC<FlexProps> = (props) => (
  <Status
    text={t`Inactive`}
    variant="grey"
    icon={<IconCircleEmpty />}
    {...props}
  />
)
